<template>
	<App title="Country Management">
		<div class="w-100 position-relative">
			<Block class="border-top border-secondary border-2 col-md-10 mx-auto">
				<template #header>
					<h3 class="block-title">Country Management</h3>
				</template>
				<!-- Country Management Form -->
				<div class="row">
					<div class="col-12">
						<div class="card border-0">
							<div class="card-header bg-light border-0">
								<h5 class="card-title mb-0">
									<i class="bi bi-globe text-secondary me-2"></i>
									Create New Country
								</h5>
							</div>
							<div class="card-body p-4">
								<Form ref="countryForm" @submit="onCountrySubmit">
									<div class="row g-3">
										<!-- Country Name -->
										<div class="col-12 col-md-6">
											<label for="countryName" class="form-label text-dark fw-normal">
												<i class="bi bi-globe me-1"></i>Country Name
											</label>
											<InputField 
												id="countryName" 
												v-model="countryData.country" 
												name="country" 
												rules="required|min:3|max:50" 
												label="Country Name" 
												placeholder="Enter country name..." 
											/>
										</div>

										<!-- Country Code -->
										<div class="col-12 col-md-6">
											<label for="countryCode" class="form-label text-dark fw-normal">
												<i class="bi bi-code me-1"></i>Country Code
											</label>
											<InputField 
												id="countryCode" 
												v-model="countryData.code" 
												name="code" 
												rules="required|min:2|max:3" 
												label="Country Code" 
												placeholder="e.g., US, UK, NG..." 
											/>
										</div>

										

										<!-- Country Manager -->
										<div class="col-12">
											<label for="countryManager" class="form-label text-dark fw-normal">
												<i class="bi bi-person-badge me-1"></i>Country Manager (Optional)
											</label>
											<SelectField 
												id="countryManager" 
												v-model="countryData.manager" 
												name="manager" 
												label="Country Manager" 
												:options="manager_options"
											>
											</SelectField>
										</div>

										<!-- Form Buttons -->
										<div class="col-12">
											<div class="d-grid gap-2 d-md-flex justify-content-md-end pt-3">
												<button 
													type="button" 
													class="btn bg-danger text-white me-2 px-4" 
													:disabled="countryLoading || countryDone" 
													@click="clearCountryForm"
												>
													<i class="bi bi-x-circle me-1"></i>Clear
												</button>
												<Button 
													id="countryAction" 
													type="submit" 
													:label="countryLabels" 
													:loading="countryLoading" 
													:done="countryDone" 
													class="btn bg-secondary text-light px-4" 
													:disabled="checkCountryInput" 
												/>
											</div>
										</div>
									</div>
								</Form>
							</div>
						</div>
					</div>
				</div>
			</Block>

			<!-- Notification Area -->
			<div class="mt-9 position-absolute top-0 end-0">
				<NotivueKeyboard v-slot="{ containersTabIndex }">
					<Notivue v-slot="item" :containersTabIndex="containersTabIndex">
						<AppNotification :item="item" />
					</Notivue>
				</NotivueKeyboard>
			</div>
		</div>
	</App>
</template>

<script lang="ts">
import $ from 'jquery'
import { useConfirm } from '@/libs/useConfirm.ts'
import locations from '@/json/location.json'
import Swal from 'sweetalert2'
import Alert from '@/vue/components/Alert.vue'
import Block from '@/vue/components/Block.vue'
import Button from '@/vue/components/Button.vue'
import Form from '@/vue/components/Form.vue'
import InputField from '@/vue/components/InputField.vue'
import SelectField from '@/vue/components/SelectField.vue'
import TextareaField from '@/vue/components/TextareaField.vue'
import App from '@/vue/templates/App.vue'
import { ButtonLabel } from '@/vue/utils/Button'
import { Options as Select2Options } from 'select2'
import { Component, Ref, Vue, toNative } from 'vue-facing-decorator'
import { Notivue, push, materialTheme, NotivueKeyboard } from 'notivue'
import AppNotification from '@/vue/components/AppNotification.vue'

@Component({
	components: {
		App,
		Form,
		Alert,
		InputField,
		Button,
		Block,
		SelectField,
		TextareaField,
		Notivue,
		AppNotification,
		NotivueKeyboard
	},
})
class Create extends Vue {
	confirm = useConfirm().confirm
	materialTheme = materialTheme

	// Country Form Data
	countryLoading = false
	countryDone = false
	countryData = {
		country: '',
		code: '',
		manager: '',
	}

	@Ref('countryForm')
	readonly countryForm!: InstanceType<typeof Form>

	// Computed Properties
	get manager_options(): Select2Options {
		return {
			placeholder: 'Select Country Manager',
			minimumResultsForSearch: Infinity,
			ajax: {
				method: 'get',
				url: this.route('admin.ajax.select.admin'),
			},
			//@ts-ignore
			templateResult: ({ id, fullname, email, text }) => (id ? $(`<span>${fullname} &nbsp; (${email})</span>`) : text),
			//@ts-ignore
			templateSelection: ({ id, fullname, email, text }) => (id ? $(`<span>${fullname} &nbsp; (${email})</span>`) : text),
		}
	}

	get countryLabels(): ButtonLabel {
		return {
			normal: 'Create Country',
			loading: 'Creating...',
			done: 'Created'
		}
	}

	get checkCountryInput(): boolean {
		return [this.countryData.country, this.countryData.code, this.countryData.manager].some(field => field.trim() === '')
	}

	// Methods
	clearCountryForm() {
		this.countryData = {
			country: '',
			code: '',
			manager: '',
		}
		this.countryForm.reset()
	}

	// Form Submission Methods
	async onCountrySubmit(payload: any) {
		this.countryLoading = true

        this.$http
			.post(this.route('country.page.create'), payload)
			.then((response) => {
				Swal.fire({
					title: 'Success!',
					text: 'Country created successfully!',
					icon: 'success',
					confirmButtonText: 'OK'
				}).then(() => {
					this.clearCountryForm()
					this.countryDone = false
					setTimeout(()=>{
						this.router.visit(this.route('country.page.group'))
					},3000)
				
				})	
			})
			.catch((e) => {
				push.error({
					title: 'Error',
					message: 'Unable to create country at this time. Please try again later.'
				})
				
			})
			.finally(() => (this.countryLoading = false))
	}
}

export default toNative(Create)
</script>